"use client";

import type { LomdaScene } from "@/features/lomdaEditorV2/lib/lomdaTypes";
import type { EditorState } from "./types";
import type { EditorStore } from "./createEditorStore";

/**
 * Per-scene CANVAS content — the only scene fields undo/redo tracks. Audio,
 * script, order, interactions, role, chapters, lomda name and metadata are
 * deliberately excluded so history only ever moves what's visible on the
 * canvas (elements, background, layout) plus which scene is in view.
 */
type SceneCanvas = Pick<
    LomdaScene,
    "id" | "elements" | "style" | "template" | "layout" | "mediaItem" | "imageUrl"
>;

export interface CanvasSnapshot {
    /** Which scene was in view. Restored first so an undo/redo always lands on
     *  the scene it changes — scene navigation is its own history step. */
    selectedSceneId: string | null;
    scenes: SceneCanvas[];
}

export const HISTORY_LIMIT = 100;

const CANVAS_KEYS = [
    "elements",
    "style",
    "template",
    "layout",
    "mediaItem",
    "imageUrl",
] as const;

export function partializeCanvas(state: EditorState): CanvasSnapshot {
    return {
        selectedSceneId: state.selectedSceneId,
        scenes: state.scenes.map((s) => ({
            id: s.id,
            elements: s.elements,
            style: s.style,
            template: s.template,
            layout: s.layout,
            mediaItem: s.mediaItem,
            imageUrl: s.imageUrl,
        })),
    };
}

/**
 * Determines whether two snapshots are "equal" — zundo skips recording a step
 * when this returns true. A step is recorded only when the canvas of an
 * existing scene changes, or the viewed scene changes. Adding/removing scenes
 * (a different scene set) is structural and never a history step.
 */
export function canvasEqual(a: CanvasSnapshot, b: CanvasSnapshot): boolean {
    if (a.scenes.length !== b.scenes.length) return true; // scene added/removed
    const bById = new Map(b.scenes.map((s) => [s.id, s]));
    for (const sa of a.scenes) {
        if (!bById.has(sa.id)) return true; // different scene set → structural
    }
    if (a.selectedSceneId !== b.selectedSceneId) return false; // navigation
    for (const sa of a.scenes) {
        const sb = bById.get(sa.id)!;
        for (const k of CANVAS_KEYS) {
            if (sa[k] !== sb[k]) return false; // canvas content changed
        }
    }
    return true;
}

/**
 * Overlay a snapshot's canvas onto the CURRENT scenes by id, leaving every
 * non-canvas field (audio, script, order, interactions…) and the scene set
 * untouched. Paused so the restore isn't itself recorded as a new step.
 * Restores scene navigation and drops a selection that no longer exists.
 */
function applyCanvasSnapshot(store: EditorStore, snap: CanvasSnapshot): void {
    const state = store.getState();
    const byId = new Map(snap.scenes.map((s) => [s.id, s]));
    const scenes = state.scenes.map((scene) => {
        const c = byId.get(scene.id);
        if (!c) return scene; // scene not in snapshot (added later) → keep as-is
        return {
            ...scene,
            elements: c.elements,
            style: c.style,
            template: c.template,
            layout: c.layout,
            mediaItem: c.mediaItem,
            imageUrl: c.imageUrl,
        };
    });

    let selectedSceneId = state.selectedSceneId;
    if (snap.selectedSceneId && scenes.some((s) => s.id === snap.selectedSceneId)) {
        selectedSceneId = snap.selectedSceneId;
    }

    let selection = state.selection;
    if (selection.kind === "element" || selection.kind === "block-image") {
        const selectedElementId = selection.elementId;
        const scene = scenes.find((s) => s.id === selectedSceneId);
        const stillThere = scene?.elements?.some(
            (e) => e.id === selectedElementId
        );
        if (!stillThere) selection = { kind: "none" };
    }

    store.temporal.getState().pause();
    store.setState({
        scenes,
        selectedSceneId,
        selection,
        activeTextEditorId: null,
        pendingCaret: null,
    });
    store.temporal.getState().resume();
}

export function undoCanvas(store: EditorStore): void {
    // While a text element is focused, Tiptap owns undo (see the shortcut + the
    // button is inert there too) so the two histories never tangle.
    if (store.getState().activeTextEditorId) return;
    const temporal = store.temporal.getState();
    const past = temporal.pastStates as CanvasSnapshot[];
    if (past.length === 0) return;
    const current = partializeCanvas(store.getState());
    applyCanvasSnapshot(store, past[past.length - 1]);
    store.temporal.setState({
        pastStates: past.slice(0, -1),
        futureStates: [...(temporal.futureStates as CanvasSnapshot[]), current],
    });
}

export function redoCanvas(store: EditorStore): void {
    if (store.getState().activeTextEditorId) return;
    const temporal = store.temporal.getState();
    const future = temporal.futureStates as CanvasSnapshot[];
    if (future.length === 0) return;
    const current = partializeCanvas(store.getState());
    applyCanvasSnapshot(store, future[future.length - 1]);
    store.temporal.setState({
        pastStates: [...(temporal.pastStates as CanvasSnapshot[]), current],
        futureStates: future.slice(0, -1),
    });
}

/**
 * Push one history step from a captured "before" snapshot. Used to coalesce a
 * whole text-edit session into a single undo. Mirrors zundo's limit handling.
 */
export function recordCanvasStep(
    store: EditorStore,
    before: CanvasSnapshot
): void {
    const temporal = store.temporal.getState();
    const past = (temporal.pastStates as CanvasSnapshot[]).slice();
    if (past.length >= HISTORY_LIMIT) past.shift();
    store.temporal.setState({
        pastStates: [...past, before],
        futureStates: [],
    });
}
